<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  DeleteOrganizationPermissions,
  Mutation,
  PermissionOrganization,
  PermissionUser,
  SetOrganizationPermissions,
  SetUserPermissions,
  User,
  UserPlatformPermissionsDataQuery,
} from '@/generated/graphql'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, ref, unref, watch } from 'vue'
import SelectField, { SelectOption } from '@/components/input/SelectField.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import OrganizationPermissionsPicker from '@/components/input/OrganizationPermissionsPicker.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import { ComponentExposed } from 'vue-component-type-helpers'

const props = defineProps<{
  modelValue?: User
}>()
defineEmits<{
  'update:modelValue': [value?: User]
}>()

const { t } = useI18n()

const fetchQuery = useQuery<UserPlatformPermissionsDataQuery, { id: string }>(
  gql`
    query UserPlatformPermissionsData($id: ID!) {
      account {
        adminOrganizations {
          id
          name
        }
        adminUser(id: $id) {
          firstname
          lastname
        }
        adminUserPermissions(userId: $id)
        adminOrganizationPermissions(userId: $id) {
          organization {
            id
            name
          }
          permissions
        }
      }
    }
  `,
  () => ({ id: props.modelValue?.id as string }),
  () => ({ enabled: !!props.modelValue }),
)
const notifySaved = ref(false)

const user = computed(() => fetchQuery.result.value?.account.adminUser)
const userPermissions = computed<PermissionUser[]>(
  () => fetchQuery.result.value?.account.adminUserPermissions || [],
)
const optionsPermissionUser = Object.values(PermissionUser).map((v: string) => ({
  title: v,
  value: v,
}))

const modelUserPermissions = ref<PermissionUser[]>([])
watch(userPermissions, (v) => (modelUserPermissions.value = v))

const userPermissionMutation = useMutation<Mutation, { command: SetUserPermissions }>(
  gql`
    mutation adminSetUserPermissions($command: SetUserPermissions!) {
      account {
        adminSetUserPermissions(command: $command)
      }
    }
  `,
  {
    refetchQueries: ['UserPlatformPermissionsData'],
  },
)
function setUserPermissions() {
  userPermissionMutation
    .mutate({
      command: {
        userId: props.modelValue?.id || '',
        permissions: unref(modelUserPermissions),
      },
    })
    .then(() => (notifySaved.value = true))
}

const orgPermissions = computed(
  () => fetchQuery.result.value?.account.adminOrganizationPermissions || [],
)
const userAlreadyInOrgIds = computed(() => orgPermissions.value.map((p) => p.organization.id))

const modelOrgPermissions = ref<{ [key: string]: PermissionOrganization[] }>({})
watch(orgPermissions, (v) =>
  v.forEach((p) => {
    modelOrgPermissions.value[p.organization.id] = p.permissions
  }),
)

const orgPermissionMutation = useMutation<Mutation, { command: SetOrganizationPermissions }>(
  gql`
    mutation adminSetOrgPermissions($command: SetOrganizationPermissions!) {
      account {
        adminSetOrganizationPermissions(command: $command)
      }
    }
  `,
  {
    refetchQueries: ['UserPlatformPermissionsData', 'AccountMenuData'],
  },
)
function setOrgPermissions(orgId: string, permissions: PermissionOrganization[]) {
  orgPermissionMutation
    .mutate({
      command: {
        orgId,
        permissions,
        userId: props.modelValue?.id || '',
      },
    })
    .then(() => (notifySaved.value = true))
}

const organizationOptions = computed<SelectOption<string>[]>(
  () =>
    fetchQuery.result.value?.account.adminOrganizations
      .filter((o) => !userAlreadyInOrgIds.value.includes(o.id))
      .map((o) => ({
        title: o.name,
        value: o.id,
      })) || [],
)
const modelAddOrg = ref<string>()
function addOrganizationPermissions() {
  orgPermissionMutation
    .mutate({
      command: {
        permissions: [],
        userId: props.modelValue?.id || '',
        orgId: unref(modelAddOrg) || '',
      },
    })
    .then(() => {
      notifySaved.value = true
      modelAddOrg.value = undefined
      fetchQuery.refetch()
    })
}

const deleteOrgMutation = useMutation<Mutation, { command: DeleteOrganizationPermissions }>(gql`
  mutation deleteOrgUserPermissions($command: DeleteOrganizationPermissions!) {
    account {
      adminDeleteOrganizationPermissions(command: $command)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const deleteOrg = async (id: string, name: string) => {
  await deleteDialog.value?.open(name, () =>
    deleteOrgMutation
      .mutate({
        command: {
          orgId: id,
          userId: props.modelValue?.id || '',
        },
      })
      .then(() => {
        fetchQuery.refetch()
      }),
  )
}

const openedOrganizations = ref([])
watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      openedOrganizations.value = []
    }
  },
)
</script>

<template>
  <v-dialog
    max-width="650"
    :model-value="!!props.modelValue"
    scrollable
    @update:model-value="() => $emit('update:modelValue', undefined)"
  >
    <v-card>
      <v-card-title>
        {{ t('component.userPermissions.title') }} - {{ user?.firstname }} {{ user?.lastname }}
      </v-card-title>
      <v-card-text>
        <select-field
          v-model="modelUserPermissions"
          :items="optionsPermissionUser"
          :label="t('component.userPermissions.field.userPermissions')"
          multiple
          @update:model-value="setUserPermissions()"
        />

        <h3>{{ t('component.userPermissions.organizationPermissions') }}</h3>
        <select-field
          v-model="modelAddOrg"
          :label="t('component.userPermissions.addOrganization')"
          :items="organizationOptions"
        >
          <template #append>
            <v-btn
              icon="add"
              color="primary"
              :loading="orgPermissionMutation.loading.value"
              @click="addOrganizationPermissions"
            />
          </template>
        </select-field>

        <v-list v-model:opened="openedOrganizations">
          <template v-for="perms in orgPermissions" :key="perms.organization.id">
            <v-list-group :value="perms.organization.id">
              <template #activator="{ props: activatorProps }">
                <v-list-item v-bind="activatorProps">
                  <v-list-item-title>
                    {{ perms.organization.name }}
                  </v-list-item-title>
                  <template #append>
                    <v-btn
                      density="compact"
                      variant="flat"
                      icon="delete"
                      @click.stop="deleteOrg(perms.organization.id, perms.organization.name)"
                    />
                  </template>
                </v-list-item>
              </template>

              <v-list-item>
                <organization-permissions-picker
                  v-model="modelOrgPermissions[perms.organization.id]"
                  :organization="perms.organization.name"
                  density="compact"
                  @update:model-value="
                    (v: PermissionOrganization[]) => setOrgPermissions(perms.organization.id, v)
                  "
                />
              </v-list-item>
            </v-list-group>
          </template>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="$emit('update:modelValue', undefined)"> Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <async-delete-dialog ref="deleteDialog" />
  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
